import { useEffect } from 'react';

import { Controller } from 'react-hook-form';
import Select from 'react-select';

import { noop } from '../../../utils/noop';
import { patchedStoryblokEditable } from '../../../utils/patchedStoryblokEditable';
import { CustomDropdownIndicator } from '../components/CustomDropdownIndicator/CustomDropdownIndicator';
import { useFormBuilderContext } from '../hooks/useFormBuilderContext';
import { createRHFControllerValidationRules } from '../utils/validation';
import {
  FormBuilderSelectErrorMessage,
  FormBuilderSelectWrapper,
  reactSelectStyleOverrides,
} from './FormBuilderSelect.styles';
import {
  FormBuilderSelectOptionStoryblok,
  FormBuilderSelectProps,
} from './FormBuilderSelect.types';

export const FormBuilderSelect = (props: FormBuilderSelectProps) => {
  const { blok } = props;
  const { options, key, label, includeInSubmitRequest, validationRules } = blok;

  const { setKeyValue, includeKeyInSubmitRequest, keyValues, hookForm } = useFormBuilderContext();

  const value = options.find((option) => option.value === keyValues[key]);

  const hookFormErrorMessage = hookForm.formState.errors[key]?.message || null;
  const errorMessage = typeof hookFormErrorMessage === 'string' ? hookFormErrorMessage : null;

  const controllerRules = createRHFControllerValidationRules(validationRules);

  useEffect(() => {
    const defaultOption = options.find((option) => option.isDefault);
    setKeyValue(key, defaultOption?.value || '');

    if (includeInSubmitRequest) {
      includeKeyInSubmitRequest(key);
    }
  }, []);

  const onOptionChange = (option: FormBuilderSelectOptionStoryblok) => {
    const url = option.link?.cached_url || option?.link.url || null;

    if (url) {
      window.open(url, option.link?.target);
    } else {
      setKeyValue(key, option.value);
    }
  };

  return (
    <FormBuilderSelectWrapper {...patchedStoryblokEditable(blok)}>
      <Controller
        name={key}
        control={hookForm.control}
        rules={controllerRules}
        render={({ field }) => (
          <Select
            {...field}
            classNamePrefix="form-select-custom"
            ref={noop}
            options={options}
            styles={reactSelectStyleOverrides}
            isSearchable={false}
            placeholder={label}
            components={{ DropdownIndicator: CustomDropdownIndicator }}
            value={value}
            onChange={(option: FormBuilderSelectOptionStoryblok) => {
              field.onChange({ target: { value: option } });
              onOptionChange(option);
            }}
          />
        )}
      />
      {!!errorMessage && (
        <FormBuilderSelectErrorMessage>{errorMessage}</FormBuilderSelectErrorMessage>
      )}
    </FormBuilderSelectWrapper>
  );
};
